import {
    containersCategoryId,
    emptyProducts,
    skeletonProducts,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/constants'
import {
    Container,
    Item,
    SectionHeaderOrProduct,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/types'
import { SelectedCategoryQuery } from '@/features/pos/components/ProductCatalog/queries/selectedCategory'
import {
    transformProduct,
    transformSubcategoryToSectionHeader,
} from '@/features/pos/components/ProductCatalog/utils/categories-with-products-transforms'
import { useHandleQueryErrorWithToast } from '@/features/pos/hooks/useHandleQueryErrorWithToast'
import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

export const useSelectedCategoryProductItems = (
    id?: string,
    containers?: readonly Container[]
): readonly Item[] => {
    const isContainersCategory = id === containersCategoryId

    const { data, loading, error } = useQuery(SelectedCategoryQuery, {
        variables: { id },
        skip: !id || isContainersCategory,
        fetchPolicy: 'cache-and-network',
    })

    useHandleQueryErrorWithToast(error, loading)

    return useMemo(() => {
        if (isContainersCategory) return containers ?? skeletonProducts

        if (data?.category?.uuid !== id) return loading ? skeletonProducts : emptyProducts

        if (!data && loading) return skeletonProducts

        if (!data?.category) return emptyProducts

        if (data.category.subcategories?.length) {
            return data.category.subcategories.reduce<SectionHeaderOrProduct[]>(
                (items, subcategory) => {
                    items.push(transformSubcategoryToSectionHeader(subcategory))

                    subcategory.products.forEach((product, index) => {
                        items.push(transformProduct(product, index))
                    })

                    return items
                },
                []
            )
        }

        return data.category.products.map(transformProduct)
    }, [containers, data, id, isContainersCategory, loading])
}
